import { forwardRef } from 'react';

/**
 * SearchField — the free-text filter over a list, queue or picker.
 *
 * Corpus, every line re-read in `the corpus` at its current number:
 * `DB:241` (the app header's search, and the pill this component
 * is named for), `:58` (the picker's typeahead, autofocused because the modal
 * opened for it), `:192` (the in-panel room filter) and `:495` (the queue
 * filter). All four are a bare `<input>` carrying `aria-label` and
 * `placeholder` bound to one string, a `value`, and `onInput` — no form, no
 * submit, no search icon and no clear affordance on any of them.
 *
 * A fifth instance the brief did not carry: `:522`, the case-list filter. It is
 * the same control at an 11px radius, 13.5px type and a `9px 14px` inset, and
 * that radius falls between `rounded-sm` (10px) and `rounded-lg` (12px) with
 * neither nearer, so no token can express it. It filters a list inside a panel,
 * as `:192` and `:495` do, so it renders as `rounded-sm` with `fill` rather than
 * becoming a fourth shape whose only claim is a radius the token layer cannot
 * draw. The fold-in therefore loses three things and not two: `rounded-sm` draws
 * `:192`'s `9px 12px`, so the inline inset narrows by 2px as well.
 *
 * `label` is one string on two attributes, exactly as all five write it. A
 * placeholder is not an accessible name, so `aria-label` is what names the
 * field; binding them together is what stops the visible prompt and the
 * announced name drifting apart, and it is why this control takes no `label`
 * element and is not `FormField`.
 *
 * Two decisions made on the caller's behalf, both pinned in
 * `search-field.test.tsx` — a behaviour recorded only in a comment is one the
 * next refactor removes:
 *
 * 1. `type="search"`. The corpus writes a bare input, which is `text` and is
 *    announced as a plain text field. The WebKit clear glyph that arrives with
 *    `search` is switched off in the stylesheet, because the corpus draws no
 *    clear affordance and that glyph answers to neither keyboard nor assistive
 *    technology. The larger consequence is Escape: browsers clear a non-empty
 *    search input rather than pass the key on, and two of the five instances
 *    (`:58` and `:192`) sit inside `aria-modal="true"` dialogs, so a filled
 *    field costs one extra Escape before the dialog closes. Worth the
 *    `searchbox` role, which is what tells someone what the field is for. jsdom
 *    implements none of that behaviour, so this is written down rather than
 *    pinned by a test — the honest form, and the reason it is written down.
 * 2. `shape` defaults to `pill`, the header instance at `:241`.
 *
 * `onChange` is per keystroke, matching the corpus's `onInput`: React's
 * `onChange` on an input is the DOM `input` event. Nothing is submitted here
 * and no key is intercepted — none of the five instances sits in a form, so
 * implicit submission is the caller's case to answer, not this atom's.
 *
 * No live region. Results are announced by the list being filtered, which owns
 * that region; a second one here would announce every keystroke twice.
 *
 * Values with no token, named rather than quietly inlined: the four paddings
 * (`:241` 9px 14px, `:58` 11px 15px, `:192` 9px 12px, `:495` 8px 13px) and the
 * 200px flex basis, 160px floor and 340px cap at `:241` and `:495`. They are
 * raw in the stylesheet, a tracked debt.
 *
 * Every string a person reads arrives from the caller.
 *
 * **Forwards its ref to the `<input>`.** Without one, a caller
 * that clears the field from outside it — `SearchSortBar`'s clear control is
 * the case — has no way to put focus back on it afterwards, and the reader
 * who pressed Clear loses their place.
 */
export interface SearchFieldProps {
  readonly value: string;
  /** Fires per keystroke. The caller narrows its own list; this returns text. */
  readonly onChange: (value: string) => void;
  /** Copy. Serves as the accessible name and as the visible prompt, both. */
  readonly label: string;
  readonly shape?: 'pill' | 'rounded-lg' | 'rounded-sm';
  /** `DB:58` — the picker's field is why the modal opened. */
  readonly autoFocus?: boolean;
  /** Fills a column (`:58`, `:192`, `:522`) instead of flexing a row (`:241`, `:495`). */
  readonly fill?: boolean;
}

/**
 * Written out rather than interpolated so `stories-coverage.test.ts` can read
 * the class names: an interpolated modifier is invisible to it, and a modifier
 * no stylesheet declares would then reach the app unstyled.
 */
const SHAPE_CLASS = {
  pill: 'rp-search-field--pill',
  'rounded-lg': 'rp-search-field--rounded-lg',
  'rounded-sm': 'rp-search-field--rounded-sm',
} as const;

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { value, onChange, label, shape = 'pill', autoFocus, fill },
  ref,
) {
  const classes = [
    'rp-search-field',
    SHAPE_CLASS[shape],
    fill === true ? 'rp-search-field--fill' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <input
      ref={ref}
      type="search"
      className={classes}
      aria-label={label}
      placeholder={label}
      value={value}
      autoFocus={autoFocus}
      onChange={(event) => onChange(event.target.value)}
    />
  );
});
